import { useEffect, useState, ReactNode } from 'react';
import { defineMessages } from '@alltrails/shared/react-intl';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import AppStore from '@alltrails/shared/components/storeBadges/AppStore';
import GooglePlay from '@alltrails/shared/components/storeBadges/GooglePlay';
import Button from '@alltrails/shared/denali/components/Button';
import classNames from 'classnames';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import CustomProvider from 'components/CustomProvider';
import * as styles from './styles/styles.module.scss';
import LegacyModal from '../LegacyModal';
import { ENTER } from '../../utils/constants/key_codes';
import logError from '../../utils/logError';

const MARKETING_MODAL_STRINGS = defineMessages({
  TEXT_LINK: { defaultMessage: 'Text link' },
  ERROR_SEND_TEXT: { defaultMessage: 'There was a problem sending the text.  Please check the number and try again.' },
  SENDING: { defaultMessage: 'Sending' },
  SENT: { defaultMessage: 'Sent' },
  ANY_NUMBER: {
    defaultMessage:
      'Please note: Any number you enter here is just for you. We won’t save your information or contact you without permission. Texting is only available for United States mobile numbers.'
  },
  HEAD_TO_THE: { defaultMessage: 'Head to the AllTrails app on your iOS or Android phone to begin navigating.' }
});

const INITIAL = 0;
const STATUS_SENDING = 1;
const STATUS_SENT = 2;

type Props = {
  color?: 'dark' | 'white';
  headerText: ReactNode;
  instructionsText: ReactNode;
  picture: ReactNode;
};

function MarketingModal({ color = 'dark', headerText, instructionsText, picture }: Props) {
  const {
    formattedDefaultMessages: { TEXT_LINK, ERROR_SEND_TEXT, SENDING, SENT, ANY_NUMBER, HEAD_TO_THE }
  } = useFormatMessage(MARKETING_MODAL_STRINGS);
  const languageRegionCode = useLanguageRegionCode();

  const [ready, toggleReady] = useState(false);
  const [visible, toggleVisible] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [status, setStatus] = useState(INITIAL);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (window.branch) {
      try {
        window.branch.init(__AT_DATA__.BRANCH_KEY, {}, branchError => {
          if (!branchError) {
            toggleReady(true);
          }
        });
      } catch (branchError) {
        logError(branchError);
      }
    }
  }, []);

  const sendSMS = () => {
    const toNumber = phoneNumber.trim();

    if (toNumber.length === 0) {
      return;
    }

    setStatus(STATUS_SENDING);

    window.branch.sendSMS(toNumber, {}, {}, (sendError: any) => {
      if (sendError) {
        setError(ERROR_SEND_TEXT);
        setStatus(INITIAL);
      } else {
        setStatus(STATUS_SENT);
      }
    });
  };

  const handleKeyUp = (e: any) => {
    e.preventDefault();
    setStatus(INITIAL);
    setError(null);

    if (e.keyCode === ENTER) {
      sendSMS();
    }
  };

  if (!visible || !ready) {
    return null;
  }

  const buttonDisabled = [STATUS_SENDING, STATUS_SENT].includes(status);

  function getButtonText() {
    switch (status) {
      case INITIAL:
        return TEXT_LINK;
      case STATUS_SENDING:
        return SENDING;
      case STATUS_SENT:
        return SENT;
      default:
        throw new Error(`missing button text for status '${status}'`);
    }
  }

  const renderEnglish = () => (
    <>
      <div>
        <h2 className={classNames(styles.title, color === 'white' ? styles.white : styles.gray)}>{headerText}</h2>
        {!error && <p className={classNames(styles.instructions, color === 'white' ? styles.white : styles.gray)}>{instructionsText}</p>}
        {error && <p className={styles.error}>{ERROR_SEND_TEXT}</p>}
        <div className={styles.form}>
          <input
            className={styles.input}
            onChange={event => setPhoneNumber(event.target.value)}
            onKeyUp={handleKeyUp}
            placeholder="### – ### – ####"
            type="tel"
            value={phoneNumber}
          />
          <Button
            text={getButtonText()}
            className={styles.button}
            disabled={buttonDisabled}
            onClick={sendSMS}
            testId={getButtonText()}
            variant="primary"
          />
        </div>
      </div>
      <div>
        <p className={styles.theFinePrint}>{ANY_NUMBER}</p>
      </div>
    </>
  );

  const { mobileBrowser } = __AT_DATA__;

  const renderNonEnglish = () => {
    const storeWidth = mobileBrowser ? 40 : 43;
    const instructionsClassName = mobileBrowser ? styles.instructionsNonEnglish : styles.instructions;

    return (
      <div>
        <h2 className={classNames(styles.title, color === 'white' ? styles.white : styles.gray)}>{headerText}</h2>
        <p className={classNames(instructionsClassName, color === 'white' ? styles.white : styles.gray)}>{HEAD_TO_THE}</p>
        <div className={styles.storeBadges}>
          <AppStore className={styles.appStore} languageRegionCode={languageRegionCode} height={storeWidth} />
          <GooglePlay languageRegionCode={languageRegionCode} height={storeWidth} />
        </div>
      </div>
    );
  };

  const content = languageRegionCode === 'en-US' ? renderEnglish() : renderNonEnglish();

  const containerClassNames = [styles.container];

  return (
    <CustomProvider>
      <LegacyModal handleBackgroundClick={() => toggleVisible(false)}>
        <div className={classNames(containerClassNames)}>
          {picture}
          <div className={styles.content}>
            <button className={styles.close} onClick={() => toggleVisible(false)} type="button" />
            {content}
          </div>
        </div>
      </LegacyModal>
    </CustomProvider>
  );
}

export default MarketingModal;
